import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TuiAlertService, TuiButton, TuiLoader, TuiTextfield } from '@taiga-ui/core';
import {
  TuiInputDateRangeModule,
  TuiInputModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/legacy';
import { TuiDayRange } from '@taiga-ui/cdk';
import { TuiSearch } from '@taiga-ui/layout';
import { Subscription } from 'rxjs';
import {
  ConsolidatedIncomePayload,
  ConsolidatedIncomeResponse,
  ReportsService,
} from '../../../core/services/reports.service';
import * as XLSX from 'xlsx';

type ConsolidatedTotals = ConsolidatedIncomeResponse['data']['totals'];
type ConsolidatedBreakdown = ConsolidatedIncomeResponse['data']['breakdown'];

interface CategoryCard {
  key: keyof ConsolidatedTotals;
  label: string;
  description: string;
  shiftCharges?: number;
  additionalCharges?: number;
  total: number;
  accent: string;
}

/**
 * Consolidated Income Report.
 *
 * Summary-only dashboard. Lists every revenue category in the system for a
 * single period (Monthly / Annually / Custom range): Registered, Non
 * Registered, Inward, Walk-in, Registration fees, Renewal fees. The
 * backend computes totals for the whole period (not just one page) so the
 * grand total is always trustworthy.
 */
@Component({
  selector: 'app-consolidated-income-report',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TuiTextfield,
    TuiButton,
    TuiLoader,
    TuiInputModule,
    TuiInputDateRangeModule,
    TuiTextfieldControllerModule,
    TuiSearch,
  ],
  templateUrl: './consolidated-income-report.component.html',
  styleUrl: './consolidated-income-report.component.scss',
  providers: [DatePipe],
})
export class ConsolidatedIncomeReportComponent implements OnInit, OnDestroy {
  private reportsService = inject(ReportsService);
  private alerts = inject(TuiAlertService);
  private datePipe = inject(DatePipe);

  loading = false;
  totals: ConsolidatedTotals | null = null;
  breakdown: ConsolidatedBreakdown | null = null;
  windowLabel = '';

  rangeFilterTypes: string[] = ['Monthly', 'Annually', 'Custom Range'];
  rangeFilterControl = new FormControl<string>('Monthly');

  monthControl = new FormControl<number>(new Date().getMonth());
  yearControl = new FormControl<number>(new Date().getFullYear());
  customRangeControl = new FormControl<TuiDayRange | null>(null);

  months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];

  years: number[] = [];

  private reportSub: Subscription | null = null;

  ngOnInit(): void {
    this.generateYears();
    this.loadReport();
  }

  ngOnDestroy(): void {
    if (this.reportSub) {
      this.reportSub.unsubscribe();
      this.reportSub = null;
    }
  }

  private generateYears(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  applyFilters(): void {
    this.loadReport();
  }

  clearFilters(): void {
    const today = new Date();
    this.rangeFilterControl.setValue('Monthly');
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());
    this.customRangeControl.setValue(null);
    this.loadReport();
  }

  private buildPayload(): ConsolidatedIncomePayload | null {
    const filterType = this.rangeFilterControl.value;
    if (filterType === 'Monthly') {
      const month = this.monthControl.value;
      const year = this.yearControl.value;
      if (month === null || year === null || year === undefined) {
        return null;
      }
      return {
        reportType: 'Monthly',
        month: Number(month) + 1,
        year: Number(year),
      };
    }
    if (filterType === 'Annually') {
      const year = this.yearControl.value;
      if (year === null || year === undefined) return null;
      return { reportType: 'Annually', year: Number(year) };
    }
    if (filterType === 'Custom Range' && this.customRangeControl.value) {
      const value = this.customRangeControl.value;
      const from = value.from;
      const to = value.to;
      if (!from || !to) return null;
      const startDate = new Date(from.year, from.month, from.day);
      const endDate = new Date(to.year, to.month, to.day);
      return {
        reportType: 'Custom',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    }
    return null;
  }

  loadReport(): void {
    const payload = this.buildPayload();
    if (!payload) {
      this.alerts
        .open('Please pick a valid period before searching.', { appearance: 'info' })
        .subscribe();
      return;
    }

    this.loading = true;

    if (this.reportSub) {
      try {
        this.reportSub.unsubscribe();
      } catch {
        // ignore
      }
      this.reportSub = null;
    }

    this.reportSub = this.reportsService.getConsolidatedIncomeReport(payload).subscribe({
      next: (response) => {
        if (response?.success && response?.data) {
          this.totals = response.data.totals;
          this.breakdown = response.data.breakdown;
          this.windowLabel = this.formatWindow(
            response.data.window.start,
            response.data.window.end,
            response.data.window.reportType,
          );
        } else {
          this.totals = null;
          this.breakdown = null;
          this.windowLabel = '';
        }
        this.loading = false;
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
      error: (err) => {
        console.error('Error loading consolidated income report:', err);
        this.loading = false;
        this.alerts
          .open('Error loading consolidated income report', { appearance: 'error' })
          .subscribe();
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
    });
  }

  private formatWindow(start: string, end: string, reportType: string): string {
    if (reportType === 'Monthly') {
      return this.datePipe.transform(new Date(start), 'MMMM yyyy') || '';
    }
    if (reportType === 'Annually') {
      return this.datePipe.transform(new Date(start), 'yyyy') || '';
    }
    const startStr = this.datePipe.transform(new Date(start), 'MM/dd/yyyy');
    const endStr = this.datePipe.transform(new Date(end), 'MM/dd/yyyy');
    return `${startStr} – ${endStr}`;
  }

  get categoryCards(): CategoryCard[] {
    if (!this.totals || !this.breakdown) return [];
    return [
      {
        key: 'registeredRevenue',
        label: 'Registered Clients',
        description: 'Shift charges + extra charges',
        shiftCharges: this.breakdown.registered.shiftCharges,
        additionalCharges: this.breakdown.registered.additionalCharges,
        total: this.totals.registeredRevenue,
        accent: 'border-l-4 border-blue-500',
      },
      {
        key: 'nonRegisteredRevenue',
        label: 'Non Registered Clients',
        description: 'Shift charges + extra charges',
        shiftCharges: this.breakdown.nonRegistered.shiftCharges,
        additionalCharges: this.breakdown.nonRegistered.additionalCharges,
        total: this.totals.nonRegisteredRevenue,
        accent: 'border-l-4 border-blue-500',
      },
      {
        key: 'inwardRevenue',
        label: 'Inward Clients',
        description: 'Shift charges only',
        shiftCharges: this.breakdown.inward.shiftCharges,
        total: this.totals.inwardRevenue,
        accent: 'border-l-4 border-green-500',
      },
      {
        key: 'walkInRevenue',
        label: 'Walk-in Clients',
        description: 'Extra charges only',
        shiftCharges: 0,
        additionalCharges: this.breakdown.walkIn.additionalCharges,
        total: this.totals.walkInRevenue,
        accent: 'border-l-4 border-amber-500',
      },
      {
        key: 'registrationIncome',
        label: 'Registration Fees',
        description: 'New registrations in period',
        total: this.totals.registrationIncome,
        accent: 'border-l-4 border-rose-500',
      },
      {
        key: 'renewalIncome',
        label: 'Renewal Fees',
        description: 'Renewals dated in period',
        total: this.totals.renewalIncome,
        accent: 'border-l-4 border-cyan-500',
      },
    ];
  }

  exportToExcel(): void {
    if (!this.totals || !this.breakdown) return;

    const dateRangeText = this.windowLabel || '';
    const generated = this.datePipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');

    const data = this.categoryCards.map((card) => ({
      'Income Category': card.label,
      'Shift Charges (LKR)': card.shiftCharges !== undefined && card.shiftCharges !== null ? card.shiftCharges.toFixed(2) : '—',
      'Additional Charges (LKR)': card.additionalCharges !== undefined && card.additionalCharges !== null ? card.additionalCharges.toFixed(2) : '—',
      'Amount (LKR)': card.total.toFixed(2),
    }));

    // Add totals row
    data.push({
      'Income Category': 'Grand Total',
      'Shift Charges (LKR)': '',
      'Additional Charges (LKR)': '',
      'Amount (LKR)': this.totals.grandTotal.toFixed(2),
    });

    const worksheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A6' });

    worksheet['!cols'] = [
      { wch: 30 }, // Income Category
      { wch: 22 }, // Shift Charges (LKR)
      { wch: 26 }, // Additional Charges (LKR)
      { wch: 18 }, // Amount (LKR)
    ];

    const headerRows = [
      ['ROYAL HOSPITAL - HOME NURSING CARE'],
      ['Consolidated Income Report'],
      [`Date Range: ${dateRangeText}`],
      [`Generated: ${generated}`],
      [],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, headerRows, { origin: 'A1' });

    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 3 } },
    ];

    const setCellStyle = (cell: string, bold: boolean, size: number) => {
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold, sz: size },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    };

    setCellStyle('A1', true, 16);
    setCellStyle('A2', true, 14);
    setCellStyle('A3', true, 12);
    setCellStyle('A4', true, 12);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Consolidated Income');

    try {
      XLSX.writeFile(workbook, `Consolidated_Income_Report_${Date.now()}.xlsx`, { bookType: 'xlsx', cellStyles: true });
    } catch (e) {
      XLSX.writeFile(workbook, `Consolidated_Income_Report_${Date.now()}.xlsx`);
    }
  }
}
